import { inject, Injectable } from '@angular/core';
import { QueryService } from '@damofire/data-commons';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { Asset, Column, System2d, Upright } from '../asset/asset.dto';
import { Inspection } from '../inspection/inspection.dto';
import { IssueCfgService } from '../config/issue-cfg.service';
import { Issue, LocalizedRiskLevel } from '../issue/issue.dto';
import { WarehouseStateSnapshotPayload } from '../snapshot/warehouse-state-snapshot.dto';
import { SnapshotSessionStore } from './snapshot-session.store';
import {
  SystemView,
  SystemViewRequest,
  UprightElevationView,
  UprightElevationViewRequest,
  WarehouseDataSource,
  WarehouseSourceKind,
  WarehouseSourceRef,
} from './warehouse-data-source';
import { buildAssetIssuesQuery, buildSystemIssuesQuery, buildSystemView } from './system-view.mapper';
import { buildUprightElevationView } from './upright-elevation-view.mapper';

/**
 * Historical state, sliced out of an already-fetched snapshot payload.
 *
 * Injects only the in-memory payload store and the issue priority config — never
 * SystemDrawingService, UprightService, AssetService, IssueService, or anything under lib/indexed,
 * so viewing history performs no live warehouse read, writes nothing offline, and leaves the
 * current source's caches untouched.
 */
@Injectable()
export class SnapshotWarehouseDataSource extends WarehouseDataSource {
  readonly kind: WarehouseSourceKind = 'snapshot';

  private readonly store = inject(SnapshotSessionStore);
  private readonly issueCfgService = inject(IssueCfgService);

  async listSystems(divisionId: string, section: string | null = null): Promise<System2d[]> {
    return [...((await this.store.requireLoaded(divisionId)).systems ?? [])]
      .filter((system: System2d & { section?: string }) => !section || system.section === section)
      .sort((a: System2d, b: System2d) => a.number - b.number);
  }

  async loadSystemView(req: SystemViewRequest): Promise<SystemView> {
    const payload: WarehouseStateSnapshotPayload = await this.store.requireLoaded(req.divisionId);
    const ref: WarehouseSourceRef = { kind: this.kind, divisionId: req.divisionId, snapshotId: this.store.activeKey()?.snapshotId };

    const system: System2d = (payload.systems ?? []).find((candidate: System2d) => candidate._id === req.systemId);

    if (!system) {
      return buildSystemView({ system: null, uprights: [], columns: [], assets: [], issues: [] }, req, ref, []);
    }

    // warehouseAssets is ww-warehouses/{division}/assets, i.e. the Upright docs.
    const uprights: Upright[] = (payload.warehouseAssets ?? []).filter((upright: Upright) => upright.systemId === system._id);

    const assetIds = new Set<string>(uprights.map((upright: Upright) => String(upright.assetId)));
    const columns: Column[] = (payload.columns ?? []).filter((column: Column) => assetIds.has(String(column.assetId)));

    const snapshotInspection: Inspection = payload.inspection ?? req.inspection;

    // A snapshot is the state of the WHOLE warehouse at a moment, so the default scope is
    // "everything still outstanding at capture time" — which includes unrepaired issues carried
    // over from earlier inspections. Narrowing to a single inspection would hide those.
    //
    // The one exception is the inspection-report view: when the page is showing the very
    // inspection this snapshot was captured for, honor that inspection's scope so resolved
    // findings from it still appear. A page showing some *other* inspection falls back to the
    // outstanding set rather than asking this payload for an inspection it never captured.
    const scopeInspection: Inspection = isSameInspection(req.inspection, snapshotInspection) ? req.inspection : undefined;

    // Same Query object the current source hands Firestore, run through the same lodash filter
    // engine the live read path already uses — the payload arrives unfiltered and division-wide.
    const issues: Issue[] = req.includeIssues ? QueryService.searchCollection<Issue>(payload.issues ?? [], buildSystemIssuesQuery(system.number, scopeInspection)).result : [];

    const priorities: LocalizedRiskLevel[] = await firstValueFrom(this.issueCfgService.getIssuePriorities(req.divisionId, undefined).pipe(take(1)));

    return buildSystemView({ system, uprights, columns, assets: payload.assets ?? [], issues }, { ...req, inspection: snapshotInspection }, ref, priorities);
  }

  async loadUprightElevationView(req: UprightElevationViewRequest): Promise<UprightElevationView> {
    const payload: WarehouseStateSnapshotPayload = await this.store.requireLoaded(req.divisionId);
    const ref: WarehouseSourceRef = { kind: this.kind, divisionId: req.divisionId, snapshotId: this.store.activeKey()?.snapshotId };
    const requestedIds = new Set<string>(req.assetIds.map(String));
    const assets: Asset[] = (payload.assets ?? []).filter((asset: Asset) => requestedIds.has(String(asset._id)));
    const previousAsset = req.previousAssetOf ? (payload.assets ?? []).find((asset: Asset) => String(asset.nextAsset) === String(req.previousAssetOf)) : null;
    if (previousAsset && !assets.some((asset: Asset) => String(asset._id) === String(previousAsset._id))) {
      assets.push(previousAsset);
    }

    const assetIds = new Set<string>([...req.assetIds.map(String), ...(previousAsset ? [String(previousAsset._id)] : [])]);
    const uprights: Upright[] = (payload.warehouseAssets ?? []).filter((upright: Upright) => assetIds.has(String(upright.assetId)));
    const columns: Column[] = (payload.columns ?? []).filter(
      (column: Column) => assetIds.has(String(column.assetId)) && (req.rowIndex === undefined || column.rowIndex === req.rowIndex),
    );

    const snapshotInspection: Inspection = payload.inspection ?? req.inspection;
    const scopeInspection: Inspection = isSameInspection(req.inspection, snapshotInspection) ? req.inspection : undefined;
    const issues: Issue[] =
      req.includeIssues && req.issueAssetId ? QueryService.searchCollection<Issue>(payload.issues ?? [], buildAssetIssuesQuery(req.issueAssetId, scopeInspection)).result : [];
    const priorities: LocalizedRiskLevel[] = await firstValueFrom(this.issueCfgService.getIssuePriorities(req.divisionId, undefined).pipe(take(1)));

    return buildUprightElevationView({ assets, uprights, columns, issues }, req, ref, priorities, snapshotInspection);
  }
}

/** Ids cross the wire as either string or number, so compare them as strings. */
function isSameInspection(a: Inspection, b: Inspection): boolean {
  return !!a?._id && !!b?._id && String(a._id) === String(b._id);
}
